import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Comic } from '@/types/comic';

interface HeroCarouselProps {
  comics: Comic[];
}

export default function HeroCarousel({ comics }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const total = comics.length;

  const go = useCallback((next: number) => setIndex((next + total) % total), [total]);

  useEffect(() => {
    if (total <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % total), 5500);
    return () => clearInterval(timer);
  }, [total]);

  if (total === 0) return null;
  const active = comics[index];

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden rounded-none md:rounded-2xl border border-background-200">
      {comics.map((comic, i) => (
        <div
          key={comic.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          <img
            src={comic.cover}
            alt={`${comic.title} 韩漫漫画推荐`}
            title={`${comic.title} 热门韩漫`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-transparent"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>

      <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-14 max-w-2xl">
        <div key={active.id} className="animate-fade-up">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-label font-medium">
              {active.status}
            </span>
            <span className="px-2.5 py-1 rounded-full bg-background-50/15 text-background-50 text-xs font-label backdrop-blur">
              本周热门推荐
            </span>
          </div>
          <h2 className="font-heading font-extrabold text-3xl md:text-5xl text-background-50 leading-tight">
            {active.title}
          </h2>
          <p className="mt-3 text-sm md:text-base text-background-100/90 line-clamp-2 md:line-clamp-3">
            {active.description}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="inline-flex items-center gap-2 px-5 md:px-7 h-11 md:h-12 rounded-md bg-primary-500 text-background-50 font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg"></i>
              立即阅读
            </Link>
            <span className="text-sm text-background-200/90">
              {active.author} · 更新至 {active.latestChapter}
            </span>
          </div>
        </div>
      </div>

      <div className="absolute z-20 bottom-6 right-6 md:bottom-14 md:right-14 flex items-center gap-3">
        <button
          type="button"
          aria-label="上一部"
          onClick={() => go(index - 1)}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur hover:bg-background-50/35 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一部"
          onClick={() => go(index + 1)}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur hover:bg-background-50/35 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>
      </div>

      <div className="absolute z-20 bottom-8 left-6 md:bottom-16 md:left-14 flex items-center gap-2">
        {comics.map((comic, i) => (
          <button
            key={comic.id}
            type="button"
            aria-label={`切换到第${i + 1}部`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-8 bg-primary-500' : 'w-3 bg-background-50/40'
            }`}
          />
        ))}
      </div>
    </section>
  );
}